import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EditVehicle = () => {
  const { vehicleId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [selectedCity, setSelectedCity] = useState('Belgaum');
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [vehicle, setVehicle] = useState(null);
  
  const [formData, setFormData] = useState({
    price: '',
    contact_phone: '',
    city: '',
    km_run_range: ''
  });

  useEffect(() => {
    fetchCities();
    fetchVehicle();
  }, [vehicleId]);

  const fetchCities = async () => {
    try {
      const res = await axios.get(`${API}/cities`);
      setCities(res.data.cities);
    } catch (error) {
      console.error('Failed to fetch cities:', error);
    }
  };

  const fetchVehicle = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/vehicles/${vehicleId}`);
      setVehicle(res.data.vehicle);
      setFormData({
        price: res.data.vehicle.price || '',
        contact_phone: res.data.vehicle.contact_phone || '',
        city: res.data.vehicle.city || '',
        km_run_range: res.data.vehicle.km_run_range || ''
      });
    } catch (error) {
      toast.error('Failed to load vehicle');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await axios.put(
        `${API}/vehicles/${vehicleId}`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Vehicle updated successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update vehicle');
    } finally {
      setSaving(false);
    }
  };

  const kmRanges = [
    '0-5,000', '5,000-10,000', '10,000-20,000', '20,000-40,000', '40,000-50,000',
    '50,000-65,000', '65,000-85,000', '85,000-100,000', '100,000+'
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-secondary-50">
        <Header cities={cities} selectedCity={selectedCity} setSelectedCity={setSelectedCity} showSearch={false} />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary-50">
      <Header cities={cities} selectedCity={selectedCity} setSelectedCity={setSelectedCity} showSearch={false} />

      <div className="px-4 md:px-8 lg:px-12 py-8">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center gap-4 mb-6">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-secondary-800">Edit Vehicle</h1>
              <p className="text-secondary-600">
                {vehicle?.brand_name} {vehicle?.model_name} - {vehicle?.registration_no}
              </p>
            </div>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Update Vehicle Details</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Vehicle Info (Read-only) */}
                <div className="bg-secondary-50 p-4 rounded-lg">
                  <h3 className="font-medium text-secondary-700 mb-3">Vehicle Information</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-secondary-500">Registration:</span>
                      <span className="ml-2 font-medium">{vehicle?.registration_no}</span>
                    </div>
                    <div>
                      <span className="text-secondary-500">Type:</span>
                      <span className="ml-2 font-medium capitalize">{vehicle?.vehicle_type}</span>
                    </div>
                    <div>
                      <span className="text-secondary-500">Brand:</span>
                      <span className="ml-2 font-medium">{vehicle?.brand_name}</span>
                    </div>
                    <div>
                      <span className="text-secondary-500">Model:</span>
                      <span className="ml-2 font-medium">{vehicle?.model_name}</span>
                    </div>
                  </div>
                </div>

                {/* Editable Fields */}
                <div>
                  <Label>Price *</Label>
                  <Input
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                    placeholder="e.g., ₹5,00,000"
                    required
                    data-testid="edit-price-input"
                  />
                </div>

                <div>
                  <Label>Contact Phone *</Label>
                  <Input
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
                    placeholder="Phone number"
                    required
                    data-testid="edit-phone-input"
                  />
                </div>

                <div>
                  <Label>City</Label>
                  <Select 
                    value={formData.city || "none"} 
                    onValueChange={(v) => setFormData({...formData, city: v === "none" ? "" : v})}
                  >
                    <SelectTrigger data-testid="edit-city-select">
                      <SelectValue placeholder="Select City" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select City</SelectItem>
                      {cities.map(city => (
                        <SelectItem key={city.id} value={city.name}>{city.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>KM Run Range</Label>
                  <Select 
                    value={formData.km_run_range || "none"} 
                    onValueChange={(v) => setFormData({...formData, km_run_range: v === "none" ? "" : v})}
                  >
                    <SelectTrigger data-testid="edit-km-select">
                      <SelectValue placeholder="Select Range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select Range</SelectItem>
                      {kmRanges.map(range => (
                        <SelectItem key={range} value={range}>{range} km</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex gap-4 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/dashboard')}
                    className="flex-1 border"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={saving}
                    className="flex-1 bg-primary-500 text-black border hover:bg-gray-100"
                    data-testid="save-vehicle-btn"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 text-black w-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default EditVehicle;
